import { h } from 'preact'
import { i18n } from 'i18n/i18n'

import { getCurrentLanguage } from 'utils/current-language'

import { SelectSingleOption } from 'common-modules/selects/calculator-select-option'
import { useSelectVisibility } from 'hooks/useSelectVisibility'

interface CalculatorCountrySelectProps {
  countries: string[]
  selectedCountryCode: string
  handleSetCountry: (countryCode: string) => void
}

const getCountryName = (countryCode: string): string => i18n(`countries.${countryCode}`)?.toString() || ''

const getCountryFlagClassName = (countryCode: string): string =>
  `icon-country-round-${countryCode} icon-country-round-${countryCode}-dims`

export const CalculatorCountrySelect = ({
  countries,
  selectedCountryCode,
  handleSetCountry,
}: CalculatorCountrySelectProps) => {
  const {
    isSelectVisible,
    toggleSelectVisibility,
    triggerRef,
    containerRef,
    listRef,
    handleTriggerKeyDown,
    handleListKeyDown,
    searchValue,
    setSearchValue,
    searchInputRef,
  } = useSelectVisibility({ enableSearch: true })

  const visibleCountries = [...countries]
    .sort((a, b) => getCountryName(a).localeCompare(getCountryName(b), getCurrentLanguage()))
    .filter((countryCode) => getCountryName(countryCode).toLowerCase().includes(searchValue.toLowerCase().trim()))

  return (
    <div
      ref={containerRef}
      className="preact-select preact-select--country"
    >
      <div
        ref={triggerRef}
        className="preact-select__trigger"
        aria-expanded={isSelectVisible}
        role="button"
        tabIndex={0}
        onClick={toggleSelectVisibility}
        onKeyDown={handleTriggerKeyDown}
        data-value={selectedCountryCode}
      >
        <span className={`preact-select__option-flag ${getCountryFlagClassName(selectedCountryCode)}`} />
        <span className="preact-select__option-name preact-select__option-name--with-flag">
          {getCountryName(selectedCountryCode)}
        </span>
        <span className="preact-select__chevron" />
      </div>
      {isSelectVisible && (
        <div
          ref={listRef}
          className="preact-select__options-list"
          role="listbox"
          onKeyDown={handleListKeyDown}
          onClick={toggleSelectVisibility}
        >
          <div
            className="preact-select__search"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              ref={searchInputRef}
              value={searchValue}
              type="text"
              className="preact-select__search-input"
              onInput={(e) => setSearchValue(e.currentTarget.value)}
            />
          </div>
          {visibleCountries.map((countryCode) => (
            <SelectSingleOption
              key={countryCode}
              option={countryCode}
              optionName={getCountryName(countryCode)}
              handleSetClickedOption={handleSetCountry}
              isWithFlags={true}
              flagClassName={getCountryFlagClassName(countryCode)}
            />
          ))}
        </div>
      )}
    </div>
  )
}
